import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';
/**
 * Animation Configuration
 * Centralizes all animation timing values and applies the global speed multiplier
 */

// Default animation speed from environment variable (default: 1.2)
// Note: For Webpack, use process.env instead of import.meta.env
const DEFAULT_ANIMATION_SPEED = parseFloat(process.env.REACT_APP_ANIMATION_SPEED) || 1.0;

/**
 * Get the current animation speed from localStorage or default
 * @returns {number} Current animation speed multiplier
 */
export const getAnimationSpeed = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
        const savedSpeed = localStorage.getItem('animationSpeed');
        if (savedSpeed !== null) {
            const parsed = parseFloat(savedSpeed);
            if (!isNaN(parsed) && parsed >= 0.2 && parsed <= 3) {
                return parsed;
            }
        }
    }
    return DEFAULT_ANIMATION_SPEED;
};

/**
 * Apply the speed multiplier to a duration
 * Higher speed values = faster animations (like YouTube speed controls)
 * @param {number} baseDuration - Base duration in milliseconds
 * @returns {number} Adjusted duration
 */
export const getAnimationDuration = (baseDuration) => {
    return Math.round(baseDuration / getAnimationSpeed());
};

/**
 * Base animation durations (in milliseconds)
 * These are the default values that will be multiplied by ANIMATION_SPEED
 */
export const BASE_DURATIONS = {
    // Card animations
    CARD_FLIP: 300,
    CARD_REVEAL: 300,
    CARD_HIDE: 300,
    CARD_SLIDE: 400,

    // Deal animations
    DEAL_DURATION: 400,
    DEAL_DELAY: 200,

    // Toast animations
    TOAST_FADE_IN: 300,
    TOAST_FADE_OUT: 300,

    // Score marker animations
    JOKER_ANIMATION: 400,

    // Game start sequence
    SEQUENCE_WAIT: 1000,
    RESULT_WAIT: 3000,
};

/**
 * Get animation durations with the global speed multiplier applied
 * Use this object to get the actual duration to use in animations
 */
export const ANIMATION_DURATIONS = {
    get CARD_FLIP() { return getAnimationDuration(BASE_DURATIONS.CARD_FLIP); },
    get CARD_REVEAL() { return getAnimationDuration(BASE_DURATIONS.CARD_REVEAL); },
    get CARD_HIDE() { return getAnimationDuration(BASE_DURATIONS.CARD_HIDE); },
    get CARD_SLIDE() { return getAnimationDuration(BASE_DURATIONS.CARD_SLIDE); },
    get DEAL_DURATION() { return getAnimationDuration(BASE_DURATIONS.DEAL_DURATION); },
    get DEAL_DELAY() { return getAnimationDuration(BASE_DURATIONS.DEAL_DELAY); },
    get TOAST_FADE_IN() { return getAnimationDuration(BASE_DURATIONS.TOAST_FADE_IN); },
    get TOAST_FADE_OUT() { return getAnimationDuration(BASE_DURATIONS.TOAST_FADE_OUT); },
    get JOKER_ANIMATION() { return getAnimationDuration(BASE_DURATIONS.JOKER_ANIMATION); },
    get SEQUENCE_WAIT() { return getAnimationDuration(BASE_DURATIONS.SEQUENCE_WAIT); },
    get RESULT_WAIT() { return getAnimationDuration(BASE_DURATIONS.RESULT_WAIT); },
};

/**
 * Log current animation configuration (for debugging)
 */
export const logAnimationConfig = () => {
    debugLog('[AnimationConfig] Speed multiplier:', getAnimationSpeed());
    debugLog('[AnimationConfig] Durations:', {
        CARD_FLIP: ANIMATION_DURATIONS.CARD_FLIP,
        CARD_SLIDE: ANIMATION_DURATIONS.CARD_SLIDE,
        DEAL_DURATION: ANIMATION_DURATIONS.DEAL_DURATION,
        DEAL_DELAY: ANIMATION_DURATIONS.DEAL_DELAY,
    });
};

export default {
    getAnimationDuration,
    getAnimationSpeed,
    ANIMATION_DURATIONS,
    BASE_DURATIONS,
    logAnimationConfig,
};
